import { useCallback, useEffect, useRef } from 'react';
import { useUnifiedAuth } from '../contexts/UnifiedAuthContext';
import { on } from '../utils/eventBus';
import { safeGetStorageItem } from '../utils/browserStorage';

const buildLoginUrl = (): string => {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  return `/login?returnTo=${encodeURIComponent(returnTo)}`;
};

export default function ReauthOverlay(): null {
  const lastEventTime = useRef(0);
  const { isAuthenticated, isLoading } = useUnifiedAuth();

  const shouldRedirectToLogin = useCallback((detail: unknown): boolean => {
    if (isLoading || window.location.pathname === '/login') {
      return false;
    }

    const hasStoredBotSpotSession = Boolean(safeGetStorageItem('localStorage', 'customAuthToken'));
    if (isAuthenticated || hasStoredBotSpotSession) {
      return true;
    }

    const eventDetail = detail && typeof detail === 'object'
      ? detail as { source?: unknown; error?: unknown }
      : {};
    const source = typeof eventDetail.source === 'string' ? eventDetail.source : '';
    const error = typeof eventDetail.error === 'string' ? eventDetail.error : '';

    return (
      (source === 'online' || source === 'visibilitychange') &&
      (error.includes('login_required') || error.includes('consent_required'))
    );
  }, [isAuthenticated, isLoading]);

  const handleRedirectToLogin = useCallback((detail: unknown) => {
    if (!shouldRedirectToLogin(detail)) {
      console.debug('[ReauthOverlay] Ignoring reauth event while no active session is present', detail);
      return;
    }

    console.log('[ReauthOverlay] Session expired, auto-redirecting to login...', detail);
    window.location.href = buildLoginUrl();
  }, [shouldRedirectToLogin]);

  useEffect(() => {
    const off = on('auth:reauth_required', (detail: unknown) => {
      const now = Date.now();
      if (now - lastEventTime.current < 1000) {
        return;
      }
      lastEventTime.current = now;

      handleRedirectToLogin(detail);
    });

    return () => {
      off && off();
    };
  }, [handleRedirectToLogin]);

  return null;
}
